import React, { useState } from 'react';
import { 
  Building2, Users, DollarSign, Award, TrendingUp, 
  CheckCircle2, Clock, Calendar, Download, ShieldCheck, 
  FileText, BarChart3, AlertCircle, ArrowUpRight 
} from 'lucide-react';
import { INSTITUTION_INFO } from '../../data/portalData';

export const DirectorDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Realistic Paystack settlements ledger
  const paystackSettlements = [
    { ref: "PSTK_LIVE_998471203", date: "18 Oct 2026", payer: "Obong Victor Okon", prog: "DBA", type: "Application Fee", amount: 25000, status: "Settled" },
    { ref: "PSTK_LIVE_771923441", date: "17 Oct 2026", payer: "Blessing Eno Archibong", prog: "EMAF", type: "Application Fee", amount: 25000, status: "Settled" },
    { ref: "PSTK_LIVE_441290382", date: "17 Oct 2026", payer: "Kufre Mfoniso James", prog: "PGD", type: "Application Fee", amount: 25000, status: "Settled" },
    { ref: "PSTK_LIVE_882019445", date: "16 Oct 2026", payer: "Idorenyin Bassey", prog: "PhD", type: "Session Tuition (50%)", amount: 425000, status: "Settled" },
    { ref: "PSTK_LIVE_331902488", date: "15 Oct 2026", payer: "Emem Udoh", prog: "DBA", type: "Acceptance Fee", amount: 50000, status: "Settled" },
    { ref: "PSTK_LIVE_110948332", date: "15 Oct 2026", payer: "Aniefiok Okon", prog: "MBA", type: "Session Tuition (Full)", amount: 550000, status: "Settled" }
  ];

  // Calculate dynamic summary figures from the 6 students
  const totalRevenue = paystackSettlements.reduce((sum, item) => sum + item.amount, 0);
  const totalCandidates = paystackSettlements.length;
  const screeningCount = paystackSettlements.filter(s => s.type.toLowerCase().includes('application')).length;
  const enrolledCount = paystackSettlements.filter(s => !s.type.toLowerCase().includes('application')).length;
  const doctoralCount = paystackSettlements.filter(s => s.prog === 'PhD' || s.prog === 'DBA').length;

  return (
    <div className="space-y-8">
      
      {/* Executive Director Header in AKSU Emblem Colors */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-6 sm:p-8 border-2 border-[#7A470E] shadow-xl flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop" 
              alt={INSTITUTION_INFO.director.name} 
              className="w-16 h-16 rounded-full object-cover border-2 border-[#7A470E] shadow-md"
            />
            <span className="absolute bottom-0 right-0 bg-[#0B2644] w-4 h-4 rounded-full border-2 border-white" />
          </div>
          <div>
            <div className="text-xs uppercase font-black tracking-widest text-[#B87A28] font-sc">
              Executive Administrative Console
            </div>
            <h2 className="text-xl sm:text-3xl font-black font-sc text-white">
              {INSTITUTION_INFO.director.name}, <span className="font-sans text-xs font-normal text-[#B87A28]">{INSTITUTION_INFO.director.qualifications}</span>
            </h2>
            <div className="text-xs text-slate-300 mt-0.5">
              {INSTITUTION_INFO.director.title} • Akwa Ibom State University
            </div>
          </div>
        </div>

        {/* Quick KPI Badge */}
        <div className="flex items-center space-x-3 text-xs">
          <div className="bg-[#030F1C] px-4 py-2 rounded-xl border border-slate-800 text-right">
            <span className="text-slate-400 block text-[10px] uppercase font-bold font-sc">Total Enrolled Cohort</span>
            <span className="text-xl font-black text-[#B87A28] font-sc">{totalCandidates} Scholars</span>
          </div>
          <div className="bg-[#06182B] px-4 py-2 rounded-xl border border-[#7A470E]/40 text-right">
            <span className="text-slate-400 block text-[10px] uppercase font-bold font-sc">Paystack Revenue YTD</span>
            <span className="text-xl font-black text-[#B87A28] font-sc">₦{totalRevenue.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white rounded-2xl p-5 border-2 border-slate-200 shadow-xs hover:border-[#7A470E] transition-colors">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-600 font-sc">Total Candidates</span>
            <Users className="w-4 h-4 text-[#0B2644]" />
          </div>
          <div className="text-3xl font-black text-[#0B2644] font-sc">{totalCandidates}</div>
          <div className="text-[11px] text-[#0B2644] font-bold mt-1 flex items-center">
            <TrendingUp className="w-3 h-3 mr-1" />
            {totalCandidates} verified student records logged
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border-2 border-slate-200 shadow-xs hover:border-[#0B2644] transition-colors">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-600 font-sc">Screening In-Progress</span>
            <Clock className="w-4 h-4 text-[#0B2644]" />
          </div>
          <div className="text-3xl font-black text-[#0B2644] font-sc">{screeningCount}</div>
          <div className="text-[11px] text-slate-600 font-medium mt-1">
            Application dossiers pending board clearance
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border-2 border-slate-200 shadow-xs hover:border-[#7A470E] transition-colors">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-600 font-sc">Enrolled & Cleared</span>
            <Award className="w-4 h-4 text-[#0B2644]" />
          </div>
          <div className="text-3xl font-black text-[#0B2644] font-sc">{enrolledCount}</div>
          <div className="text-[11px] text-slate-500 mt-1">
            2 Tuition settlements + 1 Acceptance cleared
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border-2 border-slate-200 shadow-xs hover:border-[#0B2644] transition-colors">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-600 font-sc">Doctoral Candidates</span>
            <Calendar className="w-4 h-4 text-[#0B2644]" />
          </div>
          <div className="text-3xl font-black text-[#0B2644] font-sc">{doctoralCount} Scheduled</div>
          <div className="text-[11px] text-emerald-800 font-bold mt-1">
            PhD & DBA proposal defences
          </div>
        </div>
      </div>

      {/* Main Director Section: Financial Settlement & Academic Queue */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left: Paystack Financial Settlements Ledger */}
        <div className="lg:col-span-8 bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b-2 border-slate-200 pb-4">
            <div>
              <h3 className="font-black text-lg font-sc text-[#0B2644]">
                Paystack Gateway Settlements & Bursary Clearance
              </h3>
              <p className="text-xs text-slate-500">
                Real-time electronic receipts from application fees, acceptance fees, and executive tuition.
              </p>
            </div>
            <span className="text-xs font-mono bg-emerald-50 text-[#0B2644] font-black px-2.5 py-1 rounded-lg border border-emerald-300">
              Webhook Active
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b-2 border-slate-200 text-slate-500 font-bold font-sc">
                  <th className="py-2.5 pr-2">Reference</th>
                  <th className="py-2.5 px-2">Date</th>
                  <th className="py-2.5 px-2">Candidate / Executive</th>
                  <th className="py-2.5 px-2">Track</th>
                  <th className="py-2.5 px-2">Fee Type</th>
                  <th className="py-2.5 px-2 text-right">Amount (₦)</th>
                  <th className="py-2.5 pl-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {paystackSettlements.map((trx, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="py-3 pr-2 font-mono text-slate-500 text-[11px]">{trx.ref}</td>
                    <td className="py-3 px-2 text-slate-500">{trx.date}</td>
                    <td className="py-3 px-2 font-bold text-slate-900">{trx.payer}</td>
                    <td className="py-3 px-2 font-black text-[#0B2644] font-sc">{trx.prog}</td>
                    <td className="py-3 px-2 text-slate-600">{trx.type}</td>
                    <td className="py-3 px-2 text-right font-mono font-black text-slate-900">
                      ₦{trx.amount.toLocaleString()}
                    </td>
                    <td className="py-3 pl-2 text-right">
                      <span className="px-2 py-0.5 rounded text-[10px] font-black bg-emerald-100 text-[#0B2644] font-sc">
                        {trx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50/80 font-bold">
                  <td colSpan={5} className="py-3 px-2 font-sc text-[#0B2644] text-xs">
                    Total Settled Revenue ({totalCandidates} Listed Candidates)
                  </td>
                  <td className="py-3 px-2 text-right font-mono font-black text-slate-900 text-xs">
                    ₦{totalRevenue.toLocaleString()}
                  </td>
                  <td className="py-3 pl-2 text-right">
                    <span className="px-2 py-0.5 rounded text-[10px] font-black bg-emerald-100 text-[#0B2644] font-sc">
                      100% Cleared
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Right: Academic Senate & School Governance Actions */}
        <div className="lg:col-span-4 space-y-5">
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-4">
            <h3 className="font-black text-lg font-sc text-[#0B2644] border-b-2 border-slate-200 pb-3">
              Director's Action Docket
            </h3>

            <div className="space-y-3 text-xs">
              <div className="p-3.5 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-900 font-sc">PhD Proposal Defence Docket</span>
                  <span className="text-[10px] bg-red-100 text-[#0B2644] font-black px-1.5 py-0.5 rounded">{doctoralCount} Candidates</span>
                </div>
                <p className="text-[11px] text-slate-600">
                  Panel chaired by Prof. N. A. Udoka for Idorenyin Bassey (PhD), Obong Victor Okon (DBA), and Emem Udoh (DBA).
                </p>
                <button className="text-xs text-[#0B2644] hover:text-[#7A470E] font-bold block pt-1 font-sc">
                  Endorse Panel Schedule →
                </button>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-900 font-sc">NUC Quality Assurance Audit</span>
                  <span className="text-[10px] bg-emerald-100 text-[#0B2644] font-black px-1.5 py-0.5 rounded">{totalCandidates} Dossiers Audited</span>
                </div>
                <p className="text-[11px] text-slate-600">
                  Digital audit checklist completed for 6 executive candidates across 5 programmes (PhD, DBA, MBA, EMAF, PGD).
                </p>
                <button className="text-xs text-[#0B2644] hover:text-[#7A470E] font-bold block pt-1 font-sc">
                  View NUC Dossier →
                </button>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-900 font-sc">Bursary Reconciliation</span>
                  <span className="text-[10px] bg-emerald-100 text-[#0B2644] font-black px-1.5 py-0.5 rounded">₦{totalRevenue.toLocaleString()} Cleared</span>
                </div>
                <p className="text-[11px] text-slate-600">
                  Paystack direct bank deposits verified: exactly ₦{totalRevenue.toLocaleString()} reconciled across {totalCandidates} student ledger transactions.
                </p>
                <button className="text-xs text-[#0B2644] hover:text-[#7A470E] font-bold block pt-1 font-sc">
                  Download Statement (PDF) →
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};
